<template>
  <div class="store-counter">
    <div class="counter-pill elevation-6">
      <!-- Contexte courant -->
      <div class="counter-context" :style="{ backgroundColor: themeColor }">
        <v-icon small color="white">{{ contextIcon }}</v-icon>
        <span class="context-label hidden-xs-only">{{ contextLabel }}</span>
      </div>

      <!-- Magasins affichés -->
      <div class="counter-main">
        <span class="counter-number" :style="{ color: themeColor }">{{ visibleCount }}</span>
        <!-- Mobile : ratio compact, desktop : formulation complète -->
        <span v-if="isFiltered" class="counter-ratio hidden-md-and-up">/ {{ contextTotal }}</span>
        <span class="counter-label">{{ storeWord }}</span>
        <span v-if="isFiltered" class="counter-label hidden-sm-and-down">sur {{ contextTotal }}</span>
      </div>

      <div class="counter-divider"></div>

      <!-- Total tous contextes, cliquable pour changer de contexte -->
      <div class="counter-totals">
        <button
          v-for="ctx in contexts"
          :key="ctx.value"
          type="button"
          class="total-chip hidden-xs-only"
          :class="{ 'total-chip--active': ctx.value === context }"
          :style="ctx.value === context ? { borderColor: ctx.color, color: ctx.color } : {}"
          @click="switchContext(ctx.value)"
        >
          <v-icon x-small :color="ctx.value === context ? ctx.color : '#9e9e9e'">{{ ctx.icon }}</v-icon>
          <span class="total-chip-count">{{ ctx.count }}</span>
          <span class="total-chip-label hidden-sm-and-down">{{ ctx.label }}</span>
        </button>
        <span class="grand-total hidden-sm-only">{{ totalStores }} au total</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

const CONTEXTS = {
  poissonnerie: { label: 'Poissonnerie', icon: 'mdi-fish', color: '#376374' },
  boucherie: { label: 'Boucherie', icon: 'mdi-food-steak', color: '#9d0419' }
}

export default {
  name: 'StoreCounter',
  computed: {
    ...mapState({
      context: state => state.enseignes.context
    }),
    ...mapGetters(['filteredEnseignes', 'countByContext', 'contextTotal', 'totalStores']),
    visibleCount() {
      return this.filteredEnseignes.length
    },
    isFiltered() {
      return this.visibleCount !== this.contextTotal
    },
    storeWord() {
      return this.visibleCount > 1 ? 'magasins' : 'magasin'
    },
    contextLabel() {
      return CONTEXTS[this.context].label
    },
    contextIcon() {
      return CONTEXTS[this.context].icon
    },
    themeColor() {
      return CONTEXTS[this.context].color
    },
    contexts() {
      return Object.keys(CONTEXTS).map(value => ({
        value,
        ...CONTEXTS[value],
        count: this.countByContext[value]
      }))
    }
  },
  methods: {
    switchContext(value) {
      if (value === this.context) return
      this.$store.dispatch('setContext', value)
      this.$store.dispatch('setCurrentEnseigne', {})
      this.$vuetify.theme.themes.light.primary = CONTEXTS[value].color
    }
  }
}
</script>

<style lang="scss" scoped>
.store-counter {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1400;
  pointer-events: none;
}

.counter-pill {
  display: flex;
  align-items: stretch;
  background: white;
  border-radius: 30px;
  overflow: hidden;
  border: 2px solid #e0e0e0;
  pointer-events: none;
  max-width: calc(100vw - 24px);
}

.counter-context {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  color: white;
  font-weight: 600;
  font-size: 13px;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

.counter-main {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 10px 16px;
  white-space: nowrap;
}

.counter-ratio {
  font-size: 13px;
  font-weight: 600;
  color: #9e9e9e;
  margin-left: -2px;
}

.counter-number {
  font-size: 22px;
  font-weight: 700;
  line-height: 1;
  transition: color 0.3s ease;
}

.counter-label {
  font-size: 13px;
  color: #616161;
}

.counter-divider {
  width: 1px;
  background: #eeeeee;
  margin: 8px 0;
}

.counter-totals {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px 0 12px;
}

.total-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  padding: 3px 10px;
  background: white;
  color: #757575;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f5f5;
    transform: translateY(-1px);
  }
}

.total-chip--active {
  border-width: 2px;
  background: #fafafa;
}

.total-chip-label {
  font-weight: 500;
}

.grand-total {
  font-size: 12px;
  color: #9e9e9e;
  margin-left: 4px;
  white-space: nowrap;
}

@media (max-width: 959px) {
  /* Au-dessus de l'attribution OpenStreetMap et de la barre d'accueil iOS */
  .store-counter {
    bottom: 18px;
    bottom: calc(18px + env(safe-area-inset-bottom));
  }

  .counter-pill {
    border-radius: 24px;
  }

  .counter-context {
    padding: 0 12px;
  }

  .counter-main {
    padding: 8px 12px;
  }

  .counter-number {
    font-size: 18px;
  }

  .counter-label {
    font-size: 12px;
  }

  .counter-totals {
    padding: 0 10px;
  }

  .counter-ratio {
    font-size: 12px;
  }
}
</style>
